import { ComponentType, PropsWithChildren, ReactElement } from 'react';
import { useInspectionInitializer } from './hooks/initializers/useInspectionInitializer';
import { useInspectionsInitializer } from './hooks/initializers/useInspectionsInitializer';
import { useInspectionTemplateInitializer } from './hooks/initializers/useInspectionTemplateInitializer';
import { useInspectionTemplatesInitializer } from './hooks/initializers/useInspectionTemplatesInitializer';

interface InspectionTemplateStoreProviderProps extends PropsWithChildren {
  templateId?: string;
}

export function InspectionStoreProvider({
  children,
}: PropsWithChildren): ReactElement {
  useInspectionInitializer();
  return <>{children}</>;
}

export function InspectionTemplatesStoreProvider({
  children,
}: PropsWithChildren): ReactElement {
  useInspectionTemplatesInitializer();
  return <>{children}</>;
}

export function InspectionsStoreProvider({
  children,
}: PropsWithChildren): ReactElement {
  useInspectionsInitializer();
  return <>{children}</>;
}

export function InspectionListStoreProvider({
  children,
}: PropsWithChildren): ReactElement {
  useInspectionTemplatesInitializer();
  useInspectionsInitializer();
  return <>{children}</>;
}

export function InspectionTemplateStoreProvider({
  children,
  templateId,
}: InspectionTemplateStoreProviderProps): ReactElement {
  useInspectionTemplateInitializer(templateId);
  return <>{children}</>;
}

export function withInspectionStore<P extends object>(
  WrappedComponent: ComponentType<P>
): ComponentType<P>;
export function withInspectionStore<P extends object>(
  WrappedComponent: ComponentType<P>
) {
  function InspectionStoreWrappedComponent(props: P): ReactElement {
    return (
      <InspectionStoreProvider>
        <WrappedComponent {...props} />
      </InspectionStoreProvider>
    );
  }

  InspectionStoreWrappedComponent.displayName =
    `withInspectionStore(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return InspectionStoreWrappedComponent;
}

export function withInspectionTemplatesStore<P extends object>(
  WrappedComponent: ComponentType<P>
): ComponentType<P> {
  function InspectionTemplatesStoreWrappedComponent(props: P): ReactElement {
    return (
      <InspectionTemplatesStoreProvider>
        <WrappedComponent {...props} />
      </InspectionTemplatesStoreProvider>
    );
  }

  InspectionTemplatesStoreWrappedComponent.displayName =
    `withInspectionTemplatesStore(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return InspectionTemplatesStoreWrappedComponent;
}

export function withInspectionsStore<P extends object>(
  WrappedComponent: ComponentType<P>
): ComponentType<P> {
  function InspectionsStoreWrappedComponent(props: P): ReactElement {
    return (
      <InspectionsStoreProvider>
        <WrappedComponent {...props} />
      </InspectionsStoreProvider>
    );
  }

  InspectionsStoreWrappedComponent.displayName =
    `withInspectionsStore(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return InspectionsStoreWrappedComponent;
}

export function withInspectionListStore<P extends object>(
  WrappedComponent: ComponentType<P>
): ComponentType<P> {
  function InspectionListStoreWrappedComponent(props: P): ReactElement {
    return (
      <InspectionListStoreProvider>
        <WrappedComponent {...props} />
      </InspectionListStoreProvider>
    );
  }

  InspectionListStoreWrappedComponent.displayName =
    `withInspectionListStore(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return InspectionListStoreWrappedComponent;
}
